import React, { useEffect, useState, useMemo } from "react";
import { X, Plus, Camera, AlertCircle, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import LocationPicker from "components/generic/LocationPicker";
import { deleteCreatorEquipmentPhoto } from "services/creatorService";
import DatePicker from "components/generic/DatePicker";

const S3_BASE_URL = "https://beigexmemehouse.s3.amazonaws.com/beige/";

const STEPS = [
  "Basic Equipment Information",
  "Maintenance & Location Details",
  "Equipment Photos",
];

const ProgressBar = ({ step }) => (
  <div className="flex gap-3 my-4">
    {STEPS.map((_, i) => (
      <div
        key={i}
        className={`h-2 flex-1 rounded-full ${i <= step ? "bg-[#A89C87] dark:bg-primary" : "bg-[#EFE6D8] dark:bg-secondary/20"}`}
      />
    ))}
  </div>
);

const AddEquipmentModal = ({ open, onOpenChange, mode, equipment, onSave, onUploadImages }) => {
  const [step, setStep] = useState(0);
  const [images, setImages] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savedCrewEquipmentId, setSavedCrewEquipmentId] = useState(null);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    equipmentId: "",
    equipmentName: "",
    category: "",
    manufacturer: "",
    model: "",
    modelNumber: "",
    serialNumber: "",
    description: "",
    marketPrice: "",
    rentalPrice: "",
    rentalUnit: "hour",
    available: false,
    location: null,
    lastMaintenanceDate: "",
    onRent: false, // Maps to equipment_on_maintenance
  });

  const successPreviewImage = useMemo(() => {
    if (images.length > 0) return URL.createObjectURL(images[0]);
    if (existingPhotos.length > 0) return `${S3_BASE_URL}${existingPhotos[0].file_url}`;
    return null;
  }, [images, existingPhotos]);

  const getCategoryLabel = (id) => {
    const categories = { "1": "Camera", "2": "Audio", "3": "Lighting" };
    return categories[id] || "Equipment";
  };

  useEffect(() => {
    if (open) {
      setStep(0);
      setImages([]);
      setErrors({});

      if (mode === "add") {
        setSavedCrewEquipmentId(null);
        setExistingPhotos([]);
        setForm({
          equipmentId: "",
          equipmentName: "",
          category: "",
          manufacturer: "",
          model: "",
          modelNumber: "",
          serialNumber: "",
          description: "",
          marketPrice: "",
          rentalPrice: "",
          rentalUnit: "hour",
          available: false,
          location: null,
          lastMaintenanceDate: "",
          onRent: false,
        });
      } else if (mode === "edit" && equipment) {
        setExistingPhotos(equipment.crew_equipment_photos || []);

        let parsedLocation = null;
        if (equipment.storage_location) {
          try {
            parsedLocation = typeof equipment.storage_location === 'string'
              ? JSON.parse(equipment.storage_location)
              : equipment.storage_location;
          } catch (e) {
            console.error("Error parsing location", e);
            parsedLocation = null;
          }
        }

        setForm({
          equipmentId: equipment.equipment_id || "",
          equipmentName: equipment.equipment?.equipment_name || equipment.equipment_name || "",
          category: String(equipment.category_id || ""),
          manufacturer: equipment.manufacturer || "",
          model: equipment.model || "",
          modelNumber: equipment.model_number || "",
          serialNumber: equipment.serial_number || "",
          description: equipment.description || "",
          marketPrice: equipment.market_price || "",
          rentalPrice: equipment.rental_price || "",
          rentalUnit: equipment.rental_price_type === 2 ? "day" : "hour",
          available: !!equipment.is_available_for_rent,
          location: parsedLocation,
          lastMaintenanceDate: equipment.last_maintenance_date || "",
          // If response is 1 -> show ON (true). If 0 -> show OFF (false)
          onRent: Number(equipment.equipment_on_maintenance) === 1,
        });
        setSavedCrewEquipmentId(equipment.crew_equipment_id);
      }
    }
  }, [open, mode, equipment]);

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
  };

  const validateStep = () => {
    let currentErrors = {};

    if (step === 0) {
      if (!form.equipmentName.trim()) currentErrors.equipmentName = "Required";
      if (!form.category) currentErrors.category = "Required";
      if (!form.manufacturer.trim()) currentErrors.manufacturer = "Required";
    }
    else if (step === 1) {
      if (!form.location) currentErrors.location = "Please select a location";
    }
    else if (step === 2) {
      if (images.length === 0 && existingPhotos.length === 0) {
        currentErrors.images = "Please upload at least one photo";
      }
    }

    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleFiles = (files) => {
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    const totalAllowed = 10 - existingPhotos.length;
    setImages((prev) => [...prev, ...imageFiles].slice(0, totalAllowed));
    if (errors.images) setErrors(prev => ({ ...prev, images: null }));
  };

  const removeNewImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingPhoto = async (index) => {
    const photoToDelete = existingPhotos[index];
    const photoId = photoToDelete.crew_equipment_photo_id;

    if (photoId) {
      setLoading(true);
      try {
        const response = await deleteCreatorEquipmentPhoto({ crew_equipment_photo_id: photoId });
        if (response?.status === 200 || response?.success || response?.code === 200) {
          setExistingPhotos((prev) => prev.filter((_, i) => i !== index));
        }
      } catch (err) {
        console.error("Delete photo error", err);
      }
      setLoading(false);
    } else {
      setExistingPhotos((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleAction = async () => {
    if (!validateStep()) return;

    if (step === 0) {
      setStep(1);
    } else if (step === 1) {
      setLoading(true);

      const payload = {
        equipment_name: form.equipmentName,
        category_id: Number(form.category),
        manufacturer: form.manufacturer,
        model: form.model,
        model_number: form.modelNumber,
        serial_number: form.serialNumber,
        description: form.description,
        // market_price: Number(form.marketPrice), // Commented
        // rental_price: Number(form.rentalPrice), // Commented
        // rental_price_type: form.rentalUnit === 'hour' ? 1 : 2, // Commented
        // is_available_for_rent: form.available ? 1 : 0, // Commented
        storage_location: form.location ? JSON.stringify(form.location) : null,
        condition_notes: form.description,
        is_draft: 0,
        last_maintenance_date: form.lastMaintenanceDate,
        // Send 1 if UI is ON (true), else send 0
        equipment_on_maintenance: form.onRent ? 1 : 0
      };

      if (mode === 'edit' || savedCrewEquipmentId) {
        payload.crew_equipment_id = savedCrewEquipmentId || equipment?.crew_equipment_id;
      }

      const crewId = await onSave(payload);
      if (crewId) {
        setSavedCrewEquipmentId(crewId);
        setStep(2);
      }
      setLoading(false);
    } else if (step === 2) {
      setLoading(true);
      await onUploadImages(savedCrewEquipmentId, images);
      setStep(3);
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 lg:p-0">
      <div
        className={`bg-[#FFF] dark:bg-card w-full max-w-[500px] rounded-2xl relative border border-[#E7E1DA] dark:border-border shadow-lg max-h-[90vh] overflow-y-auto ${step === 3 ? "p-4" : "p-6"}`}
      >
        {step !== 3 && (
          <button
            onClick={() => onOpenChange(false)}
            className="absolute right-5 top-5 text-gray-500 dark:text-muted-foreground hover:text-black dark:hover:text-foreground"
          >
            <X />
          </button>
        )}

        {step !== 3 && (
          <>
            <h2 className="text-lg font-semibold mb-2.5 dark:text-foreground">
              {mode === "edit" ? "Edit Equipment" : "Add New Equipment"}
            </h2>
            <ProgressBar step={step} />
          </>
        )}

        <div className="mt-4">
          {step === 0 && (
            <>
              <h3 className="font-semibold text-lg mb-4 dark:text-foreground">
                BASIC EQUIPMENT INFORMATION
              </h3>
              <label className="block text-sm font-medium mb-1 dark:text-muted-foreground">
                Equipment name *
              </label>
              <Input
                value={form.equipmentName}
                onChange={(e) => update("equipmentName", e.target.value)}
                placeholder="Enter equipment name..."
                className={`dark:bg-background dark:border-border dark:text-foreground ${errors.equipmentName ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              />

              <label className="block text-sm font-medium mt-4 mb-1 dark:text-muted-foreground">
                Category *
              </label>
              <Select
                value={form.category}
                onValueChange={(v) => update("category", v)}
              >
                <SelectTrigger
                  className={`dark:bg-background dark:border-border dark:text-foreground ${errors.category ? "border-red-500" : ""}`}
                >
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="dark:bg-card dark:border-border">
                  <SelectItem value="1">Camera</SelectItem>
                  <SelectItem value="2">Audio</SelectItem>
                  <SelectItem value="3">Lighting</SelectItem>
                </SelectContent>
              </Select>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="text-sm font-medium dark:text-muted-foreground">
                    Manufacturer *
                  </label>
                  <Input
                    value={form.manufacturer}
                    onChange={(e) => update("manufacturer", e.target.value)}
                    placeholder="Sony"
                    className={`dark:bg-background dark:border-border dark:text-foreground ${errors.manufacturer ? "border-red-500" : ""}`}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium dark:text-muted-foreground">
                    Model
                  </label>
                  <Input
                    value={form.model}
                    onChange={(e) => update("model", e.target.value)}
                    placeholder="A7S III"
                    className="dark:bg-background dark:border-border dark:text-foreground"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium dark:text-muted-foreground">
                    Model Number
                  </label>
                  <Input
                    value={form.modelNumber}
                    onChange={(e) => update("modelNumber", e.target.value)}
                    placeholder="SNY-A7S3"
                    className="dark:bg-background dark:border-border dark:text-foreground"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium dark:text-muted-foreground">
                    Serial Number
                  </label>
                  <Input
                    value={form.serialNumber}
                    onChange={(e) => update("serialNumber", e.target.value)}
                    placeholder="SNYA7S3-984"
                    className="dark:bg-background dark:border-border dark:text-foreground"
                  />
                </div>
              </div>

              <label className="block text-sm font-medium mt-4 mb-1 dark:text-muted-foreground">
                Description
              </label>
              <Textarea
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                rows={3}
                placeholder="Equipment details..."
                className="dark:bg-background dark:border-border dark:text-foreground"
              />
            </>
          )}

          {step === 1 && (
            <>
              <h3 className="font-semibold text-lg mb-4 dark:text-foreground">
                MAINTENANCE & LOCATION DETAILS
              </h3>

              {/* COMMENTED OUT MARKET AND RENTAL PRICES
              <div>
                <label className="text-sm font-medium dark:text-muted-foreground">Market Price *</label>
                <Input
                    value={form.marketPrice}
                    onChange={(e) => update("marketPrice", e.target.value)}
                    placeholder="3400"
                    className={`mt-1 dark:bg-background dark:border-border dark:text-foreground ${errors.marketPrice ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium dark:text-muted-foreground">Rental price *</label>
                <div className="flex gap-3 mt-1">
                  <Input
                    value={form.rentalPrice}
                    onChange={(e) => update("rentalPrice", e.target.value)}
                    placeholder="34"
                    className={`dark:bg-background dark:border-border dark:text-foreground h-12 ${errors.rentalPrice ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  />
                  <Select value={form.rentalUnit} onValueChange={(v) => update("rentalUnit", v)}>
                    <SelectTrigger className="w-[140px] dark:bg-background dark:border-border dark:text-foreground"><SelectValue placeholder="Per hour" /></SelectTrigger>
                    <SelectContent className="dark:bg-card dark:border-border">
                      <SelectItem value="hour">Per hour</SelectItem>
                      <SelectItem value="day">Per day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              */}

              {/* NEW FIELD: LAST MAINTENANCE DATE */}
              <div className="mb-4">
                <label className="text-sm font-medium dark:text-muted-foreground mb-1 block">
                  Last Maintenance Date
                </label>
                <DatePicker
                  value={form.lastMaintenanceDate}
                  onChange={(date) => update("lastMaintenanceDate", date)}
                  allowPast={true}
                />
              </div>

              {/* COMMENTED OUT AVAILABLE FOR RENT
              <div className="flex items-center justify-between bg-[#F6F2ED] dark:bg-secondary/20 p-3 rounded-xl mt-4">
                <span className="text-sm font-medium dark:text-foreground">Available for rent</span>
                <Switch checked={form.available} onCheckedChange={(v) => update("available", v)} />
              </div>
              */}

              {/* NEW FIELD: EQUIPMENT ON MAINTENANCE TOGGLE */}
              <div className="flex items-center justify-between bg-[#F6F2ED] dark:bg-secondary/20 p-4 rounded-xl mb-4">
                <div className="flex flex-col">
                  <span className="text-sm font-semibold dark:text-foreground">
                    Equipment on Maintenance
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Mark if gear is currently under repair/service
                  </span>
                </div>
                <Switch
                  checked={form.onRent}
                  onCheckedChange={(v) => update("onRent", v)}
                />
              </div>

              <div className="mt-4">
                <label
                  className={`text-sm font-medium mb-1 block dark:text-muted-foreground ${errors.location ? "text-red-500" : ""}`}
                >
                  Storage Location *
                </label>
                <LocationPicker
                  value={form.location}
                  onChange={(loc) => update("location", loc)}
                />
                {errors.location && (
                  <p className="text-xs text-red-500 mt-1">{errors.location}</p>
                )}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h3 className="font-semibold text-lg mb-4 dark:text-foreground">
                EQUIPMENT PHOTOS
              </h3>
              <input
                type="file"
                accept="image/*"
                multiple
                id="equip-img"
                className="hidden"
                onChange={(e) => handleFiles(Array.from(e.target.files || []))}
              />
              <div
                className={`border border-dashed rounded-xl p-8 text-center bg-[#FDFAF7] dark:bg-secondary/10 cursor-pointer text-gray-900 dark:text-foreground ${errors.images ? "border-red-500 bg-red-50" : "border-[#ECD8B9] dark:border-border"}`}
                onClick={() => document.getElementById("equip-img")?.click()}
              >
                <div className="mx-auto w-12 h-12 rounded-xl border flex items-center justify-center mb-4 bg-[#F9F4EB] dark:bg-secondary/20 border-[#ECD8B9] dark:border-border">
                  <Plus className="text-[#A59479] dark:text-primary" />
                </div>
                <p className="font-semibold">Drag & drop media, or browse</p>
                <p className="text-xs text-[#8C8C8C] dark:text-muted-foreground mt-1">
                  Add Up to 10 Product images (Current:{" "}
                  {existingPhotos.length + images.length})
                </p>
                <p className="text-xs text-[#c6c6c6] dark:text-muted-foreground mt-1">
                  Min 1600 × 1200. Max 10MB (images)
                </p>
              </div>

              {errors.images && (
                <div className="mt-2 flex items-center gap-1 text-red-500 text-sm">
                  <AlertCircle size={14} /> {errors.images}
                </div>
              )}

              {(existingPhotos.length > 0 || images.length > 0) && (
                <div className="grid grid-cols-4 gap-3 mt-4">
                  {existingPhotos.map((photo, i) => (
                    <div
                      key={`existing-${i}`}
                      className="relative aspect-square rounded-lg overflow-hidden border dark:border-border group"
                    >
                      <img
                        src={`${S3_BASE_URL}${photo.file_url}`}
                        alt="existing"
                        className="h-full w-full object-cover"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeExistingPhoto(i);
                        }}
                        className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete from server"
                      >
                        <Trash2 size={14} />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-[10px] text-white text-center py-0.5">
                        Saved
                      </div>
                    </div>
                  ))}

                  {images.map((file, i) => (
                    <div
                      key={`new-${i}`}
                      className="relative aspect-square rounded-lg overflow-hidden border border-dashed dark:border-border"
                    >
                      <img
                        src={URL.createObjectURL(file)}
                        alt="new preview"
                        className="h-full w-full object-cover"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNewImage(i);
                        }}
                        className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-1 hover:bg-black"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {step === 3 && (
            <div className="flex flex-col items-center">
              <div className="w-full aspect-[16/10] bg-[#F6F2ED] dark:bg-secondary/15 rounded-2xl relative overflow-hidden flex items-center justify-center mb-5 border border-[#E7E1DA] dark:border-border">
                {successPreviewImage ? (
                  <img
                    src={successPreviewImage}
                    alt="Preview"
                    className="h-full w-full object-contain p-3"
                  />
                ) : (
                  <Camera size={40} className="text-[#A59479] opacity-30" />
                )}
                <div className="absolute top-3 left-3">
                  <div className="bg-white/95 dark:bg-background/90 backdrop-blur-sm px-2.5 py-1 rounded-lg text-xs font-semibold border border-black/5 shadow-sm text-black dark:text-foreground">
                    {getCategoryLabel(form.category)}
                  </div>
                </div>
              </div>

              <div className="text-center px-4 mb-2">
                <h3 className="text-xl font-bold dark:text-foreground">
                  All set! Your equipment is live
                </h3>
                <p className="text-sm text-gray-500 dark:text-muted-foreground mt-2 leading-snug">
                  Your gear is now part of your inventory and ready for upcoming
                  shoots.
                </p>
              </div>
            </div>
          )}
        </div>

        <div
          className={`flex items-center mt-6 gap-3 ${step === 3 ? "flex-row border-t dark:border-border pt-4" : "justify-between"}`}
        >
          {step === 0 && (
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="dark:text-foreground dark:hover:bg-secondary/20"
            >
              Cancel
            </Button>
          )}

          {step > 0 && step < 3 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              disabled={loading}
              className="dark:text-foreground border-[#E7E1DA] dark:border-border"
            >
              Back
            </Button>
          )}

          {step < 3 ? (
            <Button
              onClick={handleAction}
              disabled={loading}
              className="bg-button-coloured px-8"
            >
              {loading ? "Processing..." : step === 2 ? "Finish" : "Next"}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1 h-11 bg-gray-50 dark:bg-secondary/10 border-none font-semibold text-black dark:text-foreground"
              >
                Close
              </Button>
              <Button
                onClick={() => onOpenChange(false)}
                className="flex-1 h-11 bg-black dark:bg-primary text-white font-semibold"
              >
                View equipment
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddEquipmentModal;
